/**
 * Asset loading context provider
 */
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { AVATAR_FILENAMES } from './avatar-utils';

interface AssetsContextValue {
  avatarImages: Record<string, string>;
  isLoading: boolean;
  error: Error | null;
}

const AssetsContext = createContext<AssetsContextValue>({
  avatarImages: {},
  isLoading: true,
  error: null,
});

interface AssetsProviderProps {
  children: ReactNode;
}

export const AssetsProvider: React.FC<AssetsProviderProps> = ({ children }) => {
  const [avatarImages, setAvatarImages] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadAvatarImages = async () => {
      const images: Record<string, string> = {};

      try {
        for (const filename of AVATAR_FILENAMES) {
          // Use dynamic import to load images
          try {
            const imagePath = require(`../assets/${filename}`);
            images[filename] = imagePath;
          } catch (err) {
            console.error(`Failed to load avatar image: ${filename}`, err);
          }
        }

        setAvatarImages(images);
        setIsLoading(false);
      } catch (err) {
        setError(err as Error);
        setIsLoading(false);
      }
    };

    loadAvatarImages();
  }, []);

  return (
    <AssetsContext.Provider value={{ avatarImages, isLoading, error }}>
      {children}
    </AssetsContext.Provider>
  );
};

export const useAssets = () => useContext(AssetsContext);
